import { mPerLat, mPerLon } from './geo';
import { jsPDF } from 'jspdf';
import { TRACK_PROFILES, trackStats, fmtDur, WP_KINDS, wpRef, wpLabel } from './track';
import { clean, mapSnapshot, overviewView } from './exporters';
import { APP_INFO } from './license';

const n1 = (v, d = 1) => (+v).toFixed(d).replace('.', ',');

// single-route report: map with route, key figures, elevation profile
export async function trackPdf(tr, t, opts = {}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const P = TRACK_PROFILES[tr.profile], s = trackStats(tr), M = 14, W = 182;
  doc.setFillColor(15, 23, 42); doc.rect(0, 0, 210, 24, 'F');
  doc.setTextColor(255); doc.setFont('helvetica', 'bold'); doc.setFontSize(15);
  doc.text(clean(`${t.trkPdfTitle}: ${tr.name}`), M, 11);
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text(clean(`${APP_INFO.name} · ${t['trkP_' + tr.profile]}`), M, 18);
  doc.setTextColor(20);
  const pts = tr.pts.map((p) => [p.lat, p.lon]);
  try {
    const img = await mapSnapshot(overviewView(pts), [{ pts, color: P.color }], { noCross: true, markers: (tr.wps || []).map((w) => ({ pt: [w.lat, w.lon], n: w.no, color: WP_KINDS[w.kind].color })) }, 900, 520);
    if (!img) throw new Error('map');
    doc.addImage(img, 'JPEG', M, 30, W, W * 520 / 900);
  } catch { /* offline: no map */ }
  let y = 30 + W * 520 / 900 + 8;
  const rows = [
    [t.trkStartAt, new Date(s.start).toLocaleString()], [t.trkEndAt, new Date(s.end).toLocaleString()],
    [t.trkLen, `${n1(s.km, 2)} km`], [t.trkTime, fmtDur(s.ms)], [`Ø ${t.trkSpeed}`, `${n1(s.kmh)} km/h`],
    [t.trkUp, `${Math.round(s.up)} m`], [t.trkDown, `${Math.round(s.down)} m`],
    [t.trkMinMax, s.min != null ? `${n1(s.min)} m / ${n1(s.max)} m` : '–'], [t.trkPoints, String(s.n)],
  ];
  doc.setFontSize(10);
  rows.forEach(([k, v], i) => {
    const x = M + (i % 2) * (W / 2), yy = y + Math.floor(i / 2) * 7;
    doc.setFont('helvetica', 'bold'); doc.text(clean(k), x, yy);
    doc.setFont('helvetica', 'normal'); doc.text(clean(v), x + 42, yy);
  });
  y += Math.ceil(rows.length / 2) * 7 + 6;
  const alt = [];
  let d = 0;
  tr.pts.forEach((p, i) => { if (i && !p.brk) d += Math.hypot((p.lat - tr.pts[i - 1].lat) * mPerLat(p.lat), (p.lon - tr.pts[i - 1].lon) * mPerLon(p.lat)); if (p.alt != null) alt.push([d, p.alt]); });
  if (alt.length > 1 && y < 250) {
    const H = Math.min(55, 285 - y - 8), lo = Math.min(...alt.map((a) => a[1])), hi = Math.max(...alt.map((a) => a[1])), span = Math.max(hi - lo, 5), dx = alt[alt.length - 1][0] || 1;
    doc.setFont('helvetica', 'bold'); doc.text(clean(t.trkProfile), M, y); y += 3;
    doc.setDrawColor(203, 213, 225); doc.rect(M, y, W, H);
    const X = (v) => M + (v / dx) * W, Y = (v) => y + H - ((v - lo) / span) * (H - 4) - 2;
    const hex = P.color.match(/\w\w/g).map((h) => parseInt(h, 16));
    doc.setDrawColor(...hex); doc.setLineWidth(0.6);
    for (let i = 1; i < alt.length; i++) doc.line(X(alt[i - 1][0]), Y(alt[i - 1][1]), X(alt[i][0]), Y(alt[i][1]));
    doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(71, 85, 105);
    doc.text(`${n1(hi)} m`, M + 1, y + 4); doc.text(`${n1(lo)} m`, M + 1, y + H - 1); doc.text(`${n1(dx / 1000, 2)} km`, M + W - 14, y + H + 4);
  }
  const wps = tr.wps || [];
  if (wps.length) {
    doc.addPage(); y = 16;
    doc.setTextColor(20); doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text(clean(`${t.wpPdfTitle} (${wps.length})`), M, y); y += 7;
    const cols = [[t.wpColNo, 18], [t.wpColType, 62], [t.trkTime, 30], [t.altLbl, 22], [t.wpColPos, 50]];
    const head = () => { doc.setFillColor(241, 245, 249); doc.rect(M, y - 4, W, 6, 'F'); doc.setFontSize(8); doc.setFont('helvetica', 'bold'); let x = M + 1; cols.forEach(([h, w]) => { doc.text(clean(h), x, y); x += w; }); y += 6; };
    head();
    wps.forEach((w) => {
      const note = w.note || w.photos || w.videos || w.memos ? doc.splitTextToSize(clean(`${w.note}${w.photos ? ` · ${w.photos} Foto` : ''}${w.videos ? ` · ${w.videos} Video` : ''}${w.memos ? ` · ${w.memos} Audio` : ''}`), W - 22) : [];
      if (y + 6 + note.length * 4 > 285) { doc.addPage(); y = 16; head(); }
      const hex = WP_KINDS[w.kind].color.match(/\w\w/g).map((h) => parseInt(h, 16));
      doc.setFillColor(...hex); doc.circle(M + 2, y - 1.2, 1.6, 'F');
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(20);
      const vals = [wpRef(tr, w), wpLabel(t, w), new Date(w.t).toLocaleTimeString(), w.alt != null ? `${n1(w.alt)} m` : '–', `${w.lat.toFixed(6)}, ${w.lon.toFixed(6)}`];
      let x = M + 5; vals.forEach((v, i) => { doc.text(clean(v), x, y); x += cols[i][1] - (i ? 0 : 4); });
      y += 4.5;
      if (note.length) { doc.setTextColor(71, 85, 105); doc.text(note, M + 5, y); y += note.length * 4; }
      doc.setDrawColor(226, 232, 240); doc.line(M, y - 2.5, M + W, y - 2.5); y += 1.5;
    });
  }
  doc.setFontSize(7); doc.setTextColor(120); doc.text(clean(`${APP_INFO.name} · ${new Date().toLocaleString()}`), M, 292);
  if (opts.blob) return doc.output('blob');
  doc.save(`${(tr.name || 'route').replace(/[^\w-]+/g, '_').slice(0, 40)}.pdf`);
}
